import { motion } from 'framer-motion'
import { Fish, Target, Crown, Gem } from 'lucide-react'
import type { AchievementLevel } from '../../types'
import { ACHIEVEMENT_LEVELS } from '../../types'

interface AchievementBadgeProps {
  level: AchievementLevel
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  showTooltip?: boolean
  className?: string
  animated?: boolean
  // Accessibility props
  ariaLabel?: string
  describedBy?: string
}

const AchievementBadge = ({
  level,
  size = 'md',
  showTooltip = true,
  className = '',
  animated = true,
  ariaLabel,
  describedBy
}: AchievementBadgeProps) => {
  const achievement = ACHIEVEMENT_LEVELS[level]

  // Size configurations
  const sizeConfig = {
    xs: {
      container: 'w-6 h-6',
      icon: 'w-3 h-3',
      tooltip: 'text-xs px-2 py-1',
      border: '1px'
    },
    sm: {
      container: 'w-8 h-8',
      icon: 'w-4 h-4',
      tooltip: 'text-xs px-2 py-1',
      border: '2px'
    },
    md: {
      container: 'w-10 h-10',
      icon: 'w-5 h-5',
      tooltip: 'text-sm px-3 py-1.5',
      border: '2px'
    },
    lg: {
      container: 'w-12 h-12',
      icon: 'w-6 h-6',
      tooltip: 'text-sm px-3 py-2',
      border: '3px'
    },
    xl: {
      container: 'w-16 h-16',
      icon: 'w-8 h-8',
      tooltip: 'text-base px-4 py-2',
      border: '3px'
    }
  }

  const config = sizeConfig[size]

  // Icon mapping
  const iconMap = {
    explorer: Fish,
    hunter: Target,
    champion: Crown,
    diamond: Gem
  }

  const IconComponent = iconMap[level]

  // Animation variants
  const badgeVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring" as const,
        stiffness: 260,
        damping: 20
      }
    },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 10
      }
    }
  }

  // Frame styles based on level
  const getFrameStyles = () => {
    const baseStyles = `
      rounded-full flex items-center justify-center relative overflow-hidden
      shadow-lg transition-all duration-300
      ${config.container}
    `

    switch (level) {
      case 'explorer':
        return `
          ${baseStyles}
          bg-gradient-to-br from-amber-600 via-amber-500 to-amber-700
          border-amber-400 shadow-amber-500/30
        `
      case 'hunter':
        return `
          ${baseStyles}
          bg-gradient-to-br from-gray-400 via-gray-300 to-gray-500
          border-gray-300 shadow-gray-400/30
        `
      case 'champion':
        return `
          ${baseStyles}
          bg-gradient-to-br from-yellow-400 via-yellow-300 to-yellow-600
          border-yellow-300 shadow-yellow-400/40
        `
      case 'diamond':
        return `
          ${baseStyles}
          bg-gradient-to-br from-cyan-300 via-blue-200 to-purple-300
          border-cyan-200 shadow-cyan-400/50
          animate-pulse
        `
      default:
        return baseStyles
    }
  }


  const tooltipId = `achievement-tooltip-${level}`

  return (
    <div className={`relative inline-block ${className}`}>
      <motion.div
        className={getFrameStyles()}
        style={{
          borderWidth: config.border,
          borderColor: achievement.frameColor
        }}
        variants={animated ? badgeVariants : undefined}
        initial={animated ? "hidden" : undefined}
        animate={animated ? (level === 'diamond' ? {
          scale: 1,
          rotate: 0,
          background: [
            'linear-gradient(45deg, #67e8f9, #a5b4fc, #c084fc)',
            'linear-gradient(90deg, #a5b4fc, #c084fc, #67e8f9)',
            'linear-gradient(135deg, #c084fc, #67e8f9, #a5b4fc)',
            'linear-gradient(180deg, #67e8f9, #a5b4fc, #c084fc)'
          ]
        } : "visible") : undefined}
        transition={level === 'diamond' ? {
          background: { duration: 3, repeat: Infinity, ease: "linear" },
          scale: { type: "spring", stiffness: 260, damping: 20 },
          rotate: { type: "spring", stiffness: 260, damping: 20 }
        } : undefined}
        whileHover={animated ? "hover" : undefined}
        role="img"
        aria-label={ariaLabel || achievement.ariaLabel}
        aria-describedby={showTooltip ? tooltipId : describedBy}
        tabIndex={0}
      >
        {/* Inner glow effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent via-white/10 to-white/20" />

        {/* Diamond sparkle effect */}
        {level === 'diamond' && (
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              background: [
                'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.8) 1px, transparent 1px)',
                'radial-gradient(circle at 80% 80%, rgba(255,255,255,0.8) 1px, transparent 1px)',
                'radial-gradient(circle at 50% 10%, rgba(255,255,255,0.8) 1px, transparent 1px)',
                'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.8) 1px, transparent 1px)'
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        )}

        {/* Icon */}
        <IconComponent
          className={`${config.icon} text-white relative z-10`}
          aria-hidden="true"
        />

        {/* Champion crown pulse */}
        {level === 'champion' && (
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-yellow-200"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        )}
      </motion.div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          id={tooltipId}
          className={`
            absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
            opacity-0 hover:opacity-100 focus-within:opacity-100
            pointer-events-none transition-opacity duration-200
            z-50
          `}
          role="tooltip"
        >
          <div
            className={`
              bg-gray-900 text-white rounded-lg shadow-xl
              ${config.tooltip} whitespace-nowrap
            `}
          >
            <div className="font-bold">{achievement.name}</div>
            <div className="text-xs opacity-75">{achievement.pointsRequired} puntos</div>
          </div>
          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
        </div>
      )}

      {/* Screen reader description */}
      <div className="sr-only">
        {achievement.srDescription}
        Requiere {achievement.pointsRequired} puntos para desbloquear.
      </div>
    </div>
  )
}

// Profile Frame Component - wraps around avatars
interface ProfileFrameProps {
  level: AchievementLevel
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  variant?: 'default' | 'list' // list variant for better visibility in lists
}

export const ProfileFrame = ({
  level,
  children,
  size = 'md',
  className = '',
  variant = 'default'
}: ProfileFrameProps) => {
  const achievement = ACHIEVEMENT_LEVELS[level]

  const sizeConfig = {
    sm: {
      padding: 'p-0.5',
      border: '2px'
    },
    md: {
      padding: 'p-0.5',
      border: '2px'
    },
    lg: {
      padding: 'p-1',
      border: '3px'
    },
    xl: {
      padding: 'p-1.5',
      border: '4px'
    }
  }

  const config = sizeConfig[size]

  const getFrameClasses = () => {
    const shadowIntensity = variant === 'list' ? 'shadow-xl' : 'shadow-lg'
    const shadowOpacity = variant === 'list' ? '50' : '30'
    const borderColors = variant === 'list' ? {
      explorer: 'border-amber-500',
      hunter: 'border-gray-400',
      champion: 'border-yellow-400',
      diamond: 'border-cyan-300'
    } : {
      explorer: 'border-amber-400',
      hunter: 'border-gray-300',
      champion: 'border-yellow-300',
      diamond: 'border-cyan-200'
    }

    const baseClasses = `
      rounded-full ${config.padding} relative
      transition-all duration-300 border-solid
    `

    switch (level) {
      case 'explorer':
        return `${baseClasses} ${shadowIntensity} shadow-amber-500/${shadowOpacity} ${borderColors.explorer}`
      case 'hunter':
        return `${baseClasses} ${shadowIntensity} shadow-gray-500/${shadowOpacity} ${borderColors.hunter}`
      case 'champion':
        return `${baseClasses} ${shadowIntensity} shadow-yellow-500/${shadowOpacity} ${borderColors.champion}`
      case 'diamond':
        return `${baseClasses} ${shadowIntensity} shadow-cyan-400/${shadowOpacity} ${borderColors.diamond}`
      default:
        return baseClasses
    }
  }

  return (
    <motion.div
      className={`${getFrameClasses()} ${className}`}
      style={{ borderWidth: config.border }}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      role="img"
      aria-label={`Marco de perfil ${achievement.name}`}
    >
      {/* Diamond rainbow animation */}
      {level === 'diamond' && (
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{
            background: [
              'linear-gradient(0deg, transparent, rgba(255,255,255,0.2), transparent)',
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              'linear-gradient(180deg, transparent, rgba(255,255,255,0.2), transparent)',
              'linear-gradient(270deg, transparent, rgba(255,255,255,0.2), transparent)'
            ]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      )}

      {/* Inner content */}
      <div className="relative rounded-full overflow-hidden">
        {children}
      </div>
    </motion.div>
  )
}

export default AchievementBadge